import type { SearchSuggestion } from '@/api/v4/search.api';
import { SearchResults } from './search-results';
import type { SearchResultsFilter } from './search.state';
import { searchResultsFilters, searchResultsFilterState } from './search.state';
import { Stack } from '@/ui/line/line';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanySuggestionsTable } from './search-suggestions/company-suggestions-table/company-suggestions-table';
import { ProjectSuggestionsTable } from './search-suggestions/project-suggestions-table/project-suggestions-table';
import { TabTableFilters } from '@/components/tab-table-filters/tab-table-filters';
import { FileSuggestionsTable } from './search-suggestions/file-suggestions-table/file-suggestions-table';
import type { SearchCounts } from './use-search-results-counts.query';
import { searchQueryState } from './search.state';
import type { SearchSuggestionQuery } from '@/api/v4/search.api';
import { styled } from 'goober';
import { EmptyState } from './components/empty-state';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';

export const SearchResultsContent = ({
  results: searchSuggestions,
  totalResults,
  hasNextPage,
  onShowMore,
  isLoading,
  counts,
}: {
  results: SearchSuggestion[];
  totalResults: number;
  hasNextPage?: boolean;
  onShowMore?: () => void;
  isLoading?: boolean;
  counts?: SearchCounts;
}) => {
  const { t } = useTranslation('default', { keyPrefix: 'globalSearch' });
  const [selectedFilter, setSelectedFilter] = useAtom(searchResultsFilterState);
  const setSearchQuery = useSetAtom(searchQueryState);

  const getResultsQuantityPerType = useCallback(
    (type: SearchResultsFilter) => {
      if (!counts) {
        if (type === 'all') {
          return searchSuggestions.length;
        }
        return searchSuggestions.filter(result => result.type === type).length;
      }
      switch (type) {
        case 'organization':
          return counts.companies;
        case 'project':
          return counts.projects;
        case 'file':
          return counts.files;
        case 'team':
          return counts.teams;
        case 'tag':
          return counts.tags;
        default:
          return 0;
      }
    },
    [counts, searchSuggestions],
  );

  const filtersConfig = useMemo(
    () =>
      searchResultsFilters.map(filter => ({
        label: t(`filters.${filter}`),
        type: filter,
        isActive: selectedFilter === filter,
        resultsQuantity:
          filter === 'all'
            ? (counts?.total ?? searchSuggestions.length)
            : getResultsQuantityPerType(filter),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getResultsQuantityPerType,
      searchSuggestions.length,
      selectedFilter,
      counts?.total,
    ],
  );

  const getContent = useCallback(
    (selectedFilter: SearchResultsFilter, filteredData: SearchSuggestion[]) => {
      switch (selectedFilter) {
        case 'project':
          return (
            <ProjectSuggestionsTable
              data={filteredData}
              hasNextPage={hasNextPage}
              onShowMore={onShowMore}
            />
          );
        case 'organization':
          return (
            <CompanySuggestionsTable
              data={filteredData}
              hasNextPage={hasNextPage}
              onShowMore={onShowMore}
            />
          );
        case 'file':
          return (
            <FileSuggestionsTable
              data={filteredData}
              hasNextPage={hasNextPage}
              onShowMore={onShowMore}
              isLoading={isLoading}
            />
          );
        default:
          return (
            <>
              <SearchResults
                results={filteredData}
                hasNextPage={hasNextPage && selectedFilter === 'all'}
                onShowMore={onShowMore}
                isLoading={isLoading}
              />
            </>
          );
      }
    },
    [hasNextPage, onShowMore, isLoading, totalResults],
  );

  const filteredResults = useMemo(() => {
    if (selectedFilter === 'all') {
      return searchSuggestions;
    }

    return searchSuggestions.filter(result => result.type === selectedFilter);
  }, [searchSuggestions, selectedFilter]);

  const searchPhrase = useAtomValue(searchQueryState).search ?? '';

  const onFilterClick = (filter: SearchResultsFilter) => {
    setSelectedFilter(filter);
    setSearchQuery(prev => ({
      ...prev,
      entities: [filter] as NonNullable<SearchSuggestionQuery['entities']>,
      page: 1,
    }));
  };

  const isEmpty = !isLoading && filteredResults.length === 0;

  return (
    <Stack gap="30px">
      <CenteredTabFilters
        filters={filtersConfig}
        onFilterClick={onFilterClick}
      />
      <div key={selectedFilter}>
        {isEmpty ? (
          <EmptyState searchPhrase={searchPhrase} />
        ) : (
          getContent(selectedFilter, filteredResults)
        )}
      </div>
    </Stack>
  );
};

const CenteredTabFilters = styled(TabTableFilters<SearchResultsFilter>)`
  width: 100%;
  justify-content: center;
  column-gap: 52px;
  padding: 12px 0 8px 0;
`;
